import { useState } from 'react';
import { Alert, Collapse, Stack, Typography } from '@mui/material';
import ExpandMore from '@mui/icons-material/ExpandMore';
import { useTranslations } from '@/i18n/use-translations';
import { useMediaAgentRecovery, useMediaAgentRecoveryVisible } from '@/hooks/use-media-agent-recovery';
import { useLiveContextStateWithLivePlayState } from '@/hooks/use-live-play-state';
import { useGetFlowMediaAgentQuery } from '@/state/queries/playout/use-get-flow-media-agent';
import {
  isMediaAgentBackupSlidesActive,
  isMediaAgentRecoveryEngaged,
  resolveMediaAgentRecoveryCopy,
  resolveMediaAgentRecoveryForUi,
} from '@/helpers/media-agent-recovery.helper';
import type { MediaAgentRecoveryV1 } from '@/types/player/perform-control-v1.types.ts';
import type { LiveContextStateDto } from '@/state/queries/player/use-get-live-context-state';

const BACKUP_SLIDES_EPISODE: MediaAgentRecoveryV1 = {
  active: true,
  phase: 'backup_slides',
  since: '1970-01-01T00:00:00.000Z',
  controlHealthy: false,
  playoutHealthy: false,
  playoutMode: 'imageSequence',
};

/**
 * Prominent live-preview banner while slide control or the live slide feed is impaired.
 * Non-dismissible — it clears itself when the service reports the episode healthy again.
 */
export function FlowControlMediaAgentRecoveryAlert({
  entityRef,
  flowId,
  recoveryFromGet,
  liveContextState,
}: {
  entityRef?: string;
  /** Flow whose media-agent entitlement and switch decide whether backup chrome applies. */
  flowId?: string;
  recoveryFromGet?: MediaAgentRecoveryV1;
  liveContextState?: LiveContextStateDto;
}) {
  const t = useTranslations();
  const { data: mediaAgent } = useGetFlowMediaAgentQuery(flowId);
  const mergedLiveContextState = useLiveContextStateWithLivePlayState(entityRef, liveContextState);
  const backupSlidesActive = isMediaAgentBackupSlidesActive(mergedLiveContextState?.playState);
  const recoveryFromStore = useMediaAgentRecovery(entityRef, recoveryFromGet);
  const recovery = backupSlidesActive ? undefined : resolveMediaAgentRecoveryForUi(recoveryFromStore);
  const recoveryVisible = useMediaAgentRecoveryVisible(recovery);

  // Rendition playout is the normal mode when the flow is not using a media agent.
  if (mediaAgent && !mediaAgent.active) {
    return null;
  }

  if (backupSlidesActive) {
    const copy = resolveMediaAgentRecoveryCopy(BACKUP_SLIDES_EPISODE);
    return (
      <CollapsibleRecoveryAlert
        title={t(copy.titleKey, undefined, copy.titleFallback)}
        body={t(copy.bodyKey, undefined, copy.bodyFallback)}
      />
    );
  }

  if (!recoveryVisible || !recovery || !isMediaAgentRecoveryEngaged(recovery)) {
    return null;
  }

  const copy = resolveMediaAgentRecoveryCopy(recovery);

  return (
    <CollapsibleRecoveryAlert
      title={t(copy.titleKey, undefined, copy.titleFallback)}
      body={t(copy.bodyKey, undefined, copy.bodyFallback)}
    />
  );
}

function CollapsibleRecoveryAlert({ title, body }: { title: string; body: string }) {
  const t = useTranslations();
  const [expanded, setExpanded] = useState(false);

  const toggle = () => setExpanded((open) => !open);

  return (
    <Alert
      severity="warning"
      sx={{
        'width': '100%',
        'flexShrink': 0,
        'py': 0,
        'mb': 1,
        'alignItems': 'center',
        '& .MuiAlert-message': { width: '100%', py: 0.5 },
      }}
    >
      <Stack spacing={0.5}>
        <Stack
          direction="row"
          alignItems="center"
          spacing={0.5}
          role="button"
          tabIndex={0}
          aria-expanded={expanded}
          aria-label={expanded ? t('common.collapseInfo') : t('common.expandInfo')}
          onClick={toggle}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              toggle();
            }
          }}
          sx={{ cursor: 'pointer' }}
        >
          <Typography variant="body2" sx={{ fontWeight: 600, flex: 1, minWidth: 0 }}>
            {title}
          </Typography>
          <ExpandMore
            fontSize="small"
            sx={{ transform: expanded ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }}
          />
        </Stack>
        <Collapse in={expanded}>
          <Typography variant="body2">{body}</Typography>
        </Collapse>
      </Stack>
    </Alert>
  );
}
